import React, { FC, useEffect, useState } from 'react';
import { IVaultItem, IVaultItemInfo } from '@app/pages/passwordManager/interfaces';
import { PageLoader, PasslyButton, PasslyTabContent } from '@app/components';
import { useTypedTranslation } from '@app/hooks';
import { genericErrorToast, getActiveVaultPermission, hasOrgPermission, resolveOrgName } from '@app/helpers';
import { AxiosError, AxiosResponse } from 'axios';
import { ManageVault, vaultAdmin, vaultLaunch, vaultRead } from '@app/store/constants';
import { useDispatch, useSelector } from 'react-redux';
import { PasslyTab, PasslyTabs } from '@app/componentsV2';
import SwipeableViews from 'react-swipeable-views';
import VaultInformation from '@app/pages/passwordManager/components/vaultInfo/vaultInformation.component';
import VaultAccess from '@app/pages/passwordManager/components/vaultInfo/vaultAccess.component';
import { setExceptionHandler } from '@app/store/actions';
import { ISelectIdentities, IVaultPasswordPolicy } from '@app/models';
import { vaultsServiceNew } from '@app/services';
import { Box, Grid, TableCell } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import SelectIdentitiesV2 from '@app/componentsV2/Modal/selectIdentitiesV2.component';

interface IVaultsSectionProps {
    folderId: string;
    vaultId: string;
    passwordPolicies: IVaultPasswordPolicy[];
    allVaults: IVaultItem[];
    changeVaultNameAfterEdit: (vaultId: string, newDisplayName: string) => void;
}

const VaultsInfoSection: FC<IVaultsSectionProps> = ({ vaultId, folderId, passwordPolicies, allVaults, changeVaultNameAfterEdit }) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const [rows, setRows] = useState<IVaultItem[]>([]);
    const [vaultInfo, setVaultInfo] = useState<IVaultItemInfo | null>(null);

    const userVaultsPermissions = useSelector((state: any) => state.signIn.currentUser.vaultPermissions);
    const permissions = userVaultsPermissions ? getActiveVaultPermission(userVaultsPermissions[vaultId]) : new Set<string>();
    const manageOnly = !(permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && hasOrgPermission([ManageVault]);
    const [tabIndex, setTabIndex] = React.useState(manageOnly ? 1 : 0);
    const [loadingVaultInfo, setLoadingVaultInfo] = useState(false);
    const [showAddGroup, setShowAddGroup] = useState(false);

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        setLoadingVaultInfo(true);

        if  (vaultId) {
            vaultsServiceNew.getVaultById(vaultId, handleGetVaultByIdError, handleGetVaultByIdSuccess);
        } else {
            setVaultInfo(null);
        }
    }, [vaultId]);

    const handleGetVaultByIdSuccess = (response: AxiosResponse) => {
        setLoadingVaultInfo(false);
        setVaultInfo(response.data);
        setTabIndex(manageOnly ? 1 : 0);
    };
    const handleGetVaultByIdError = (error: AxiosError<any>) => {
        setLoadingVaultInfo(false);
        genericErrorToast(error);
    };

    const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: number) => {
        permissions?.has(vaultRead) || permissions?.has(vaultLaunch) ? setTabIndex(newValue) : setTabIndex(newValue + 1);
    };

    const handleChangeTabIndex = (index: number) => {
        permissions?.has(vaultRead) || permissions?.has(vaultLaunch) ? setTabIndex(index) : setTabIndex(index + 1);
    };

    const getPasswordsView = () => {
        return (
            <div>
                <div className='vaults__table-body'>
                    111
                    {/*                    <PasswordListComponent
                        permissions={permissions}
                        vaultId={vaultId}
                        isIndividual={vault.VaultType === IndividualVaultType}
                        vaultName={vaultName}
                        folderId={selectedFolder}
                        queryPasswordId={String(queryPasswordId)}
                        queryPasswordName={String(queryPasswordName)}
                        querySharedLink={querySharedLink}
                        filterString={searchableValue}
                        searchType={searchType}
                        openPasswordCallback={openPasswordDetailsModal}
                        foldersLoading={foldersLoading}
                    />*/}
                </div>
            </div>
        );
    };

    const showAddGroupsModal = (show = true) => {
        setShowAddGroup(show);
    };

    return (
        <>
            {!loadingVaultInfo && vaultInfo && (
                <>
                    <div className='vaults__info-header'>
                        <div className='vaults__info-title'>{vaultInfo.displayName}</div>
                        <div className='vaults__info-id'>
                            <div className='vaults__info-id-left'>
                                {translation('VAULT_ID')}
                                <strong>{vaultInfo.id}</strong>
                            </div>
                            <div className='vaults__info-id-right'>
                                {translation('VAULT_FOLDERS')} <strong>{vaultInfo.folders?.length}</strong>
                                {translation('VAULT_PASSWORDS')} <strong>{vaultInfo.passwordsCount}</strong>
                            </div>
                        </div>
                        {vaultInfo.description && (
                            <div className='vaults__info-description'>
                                <div>{translation('DESCRIPTION')}</div>
                                <strong>{vaultInfo.description}</strong>
                            </div>
                        )}
                    </div>

                    {(permissions?.has(vaultAdmin) || hasOrgPermission([ManageVault])) && (
                        <PasslyTabs
                            value={permissions?.has(vaultRead) || permissions?.has(vaultLaunch) ? tabIndex : tabIndex - 1}
                            onChange={handleChangeTab}
                            withLongLine
                        >
                            {(permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && (
                                <PasslyTab label={translation('VAULT_PASSWORDS_TAB')} />
                            )}
                            <PasslyTab label={translation('VAULT_INFO_TAB')} />
                            <PasslyTab label={translation('VAULT_ACCESS')} />
                        </PasslyTabs>
                    )}
                    {permissions?.has(vaultAdmin) || hasOrgPermission([ManageVault]) ? (
                        <SwipeableViews index={tabIndex} onChangeIndex={handleChangeTabIndex}>
                            <PasslyTabContent value={tabIndex} index={0}>
                                {(permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && getPasswordsView()}
                            </PasslyTabContent>

                            <PasslyTabContent value={tabIndex} index={1}>
                                <VaultInformation
                                    vaultInfo={vaultInfo}
                                    passwordPolicies={passwordPolicies}
                                    allVaults={allVaults}
                                    setVaultInfo={setVaultInfo}
                                    changeVaultNameAfterEdit={changeVaultNameAfterEdit}
                                />
                            </PasslyTabContent>
                            {!vaultInfo.isIndividual && (
                                <PasslyTabContent value={tabIndex} index={2}>
                                    {tabIndex === 2 && (permissions?.has(vaultAdmin) || hasOrgPermission([ManageVault]) || manageOnly) && (
                                        <div className='vaults__group-add'>
                                            <PasslyButton color='primary' variant='contained' onClick={showAddGroupsModal}>
                                                <Add color='inherit' />
                                                {translation('ADD_GROUP')}
                                            </PasslyButton>
                                        </div>
                                    )}

                                    <div className='vaults__access'>
                                        <VaultAccess
                                            vaultId={vaultInfo.id}
                                            groups={vaultInfo.groups}
                                            showAddGroup={showAddGroup}
                                            showAddGroupsModal={showAddGroupsModal}
                                        />
                                    </div>
                                </PasslyTabContent>
                            )}
                        </SwipeableViews>
                    ) : (
                        getPasswordsView()
                    )}
                    <div>{/*<PasslyTable headerItems={} rows={} rowsCount={} onFetch={}/>*/}</div>
                </>
            )}

            {loadingVaultInfo && <PageLoader useOverlay={true} isAbsolutePosition={true} />}
        </>
    );
};

export default VaultsInfoSection;
